import React, { useState } from 'react';
import { FaTruck, FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaGasPump } from 'react-icons/fa';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import '../styles/Tankers.css';

const initialTankers = [
  { id: 1, name: 'Tanker A', fuelType: 'JET A-1',     capacity: 1000, level: 720, unit: 'Gal', status: 'available', location: 'Apron 1', serialNo: 'UAS-T001' },
  { id: 2, name: 'Tanker B', fuelType: 'JET A-1',     capacity: 1000, level: 450, unit: 'Gal', status: 'in-use',   location: 'Apron 2', serialNo: 'UAS-T002' },
  { id: 3, name: 'Tanker C', fuelType: 'AVGAS 100LL', capacity: 800,  level: 144, unit: 'Gal', status: 'available', location: 'Hangar 3', serialNo: 'UAS-T003' },
];

function TankerForm({ tanker, onSave, closeModal }) {
  const [form, setForm] = useState(tanker || { name: '', fuelType: 'JET A-1', capacity: 1000, level: 0, unit: 'Gal', status: 'available', location: '', serialNo: '' });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ ...form, id: tanker?.id || Date.now() }); closeModal(); }}>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Tanker name</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Tanker D" />
        </div>
        <div className="form-row">
          <label className="form-label">Serial No.</label>
          <input className="form-input" value={form.serialNo} onChange={e => set('serialNo', e.target.value)} placeholder="UAS-T00X" />
        </div>
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Fuel type</label>
          <select className="form-input" value={form.fuelType} onChange={e => set('fuelType', e.target.value)}>
            <option>JET A-1</option>
            <option>AVGAS 100LL</option>
            <option>JET A</option>
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Unit</label>
          <select className="form-input" value={form.unit} onChange={e => set('unit', e.target.value)}>
            <option>Gal</option>
            <option>L</option>
          </select>
        </div>
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Capacity ({form.unit})</label>
          <input className="form-input" type="number" value={form.capacity} onChange={e => set('capacity', Number(e.target.value))} min={1} required />
        </div>
        <div className="form-row">
          <label className="form-label">Current level ({form.unit})</label>
          <input className="form-input" type="number" value={form.level} onChange={e => set('level', Number(e.target.value))} min={0} max={form.capacity} required />
        </div>
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Location</label>
          <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="Apron 1..." />
        </div>
        <div className="form-row">
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="available">Available</option>
            <option value="in-use">In Use</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
      </div>
      <div className="modal-actions">
        <button type="submit" className="btn btn-primary"><FaSave /> {tanker ? 'Save changes' : 'Add tanker'}</button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
      </div>
    </form>
  );
}

function DeleteConfirm({ tanker, onDelete, closeModal }) {
  return (
    <div className="delete-confirmation">
      <p>Delete <strong>{tanker.name}</strong>?</p>
      <p className="warning-text">This cannot be undone.</p>
      <div className="modal-actions">
        <button className="btn btn-danger" onClick={() => { onDelete(tanker.id); closeModal(); }}>Delete</button>
        <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
      </div>
    </div>
  );
}

export default function Tankers() {
  const [tankers, setTankers] = useState(initialTankers);
  const { openModal } = useModal();
  const { success } = useToast();

  const handleSave = (t) => {
    setTankers(prev => {
      const exists = prev.find(x => x.id === t.id);
      return exists ? prev.map(x => x.id === t.id ? t : x) : [...prev, t];
    });
    success(`${t.name} saved`);
  };

  const handleDelete = (id) => {
    setTankers(prev => prev.filter(t => t.id !== id));
    success('Tanker removed');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Tankers</h1>
          <p className="page-subtitle">Manage your fuel bowser fleet</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal('Add tanker', <TankerForm onSave={handleSave} />, 'md')}>
          <FaPlus /> Add tanker
        </button>
      </div>

      <div className="tankers-grid">
        {tankers.map(t => {
          const pct = Math.round((t.level / t.capacity) * 100);
          const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
          return (
            <div key={t.id} className="tanker-card">
              <div className="tanker-card-header">
                <div className="tanker-card-title">
                  <FaTruck />
                  <span>{t.name}</span>
                </div>
                <div className={`tanker-status-badge ${t.status}`}>
                  {t.status === 'in-use' ? 'In Use' : t.status === 'maintenance' ? 'Maintenance' : 'Available'}
                </div>
              </div>

              <div className="tanker-card-meta">
                <div><FaGasPump /> {t.fuelType}</div>
                <div>📍 {t.location || '—'}</div>
                <div>S/N: {t.serialNo || '—'}</div>
              </div>

              <div className="fl-bar-section">
                <div className="fuel-bar-track large">
                  <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                </div>
                <div className="fl-bar-labels">
                  <span style={{ color: barColor, fontWeight: 700 }}>{pct}%</span>
                  <span className="fl-gal">{t.level} / {t.capacity} {t.unit}</span>
                </div>
              </div>

              <div className="tanker-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => openModal(`Edit ${t.name}`, <TankerForm tanker={t} onSave={handleSave} />, 'md')}>
                  <FaEdit /> Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => openModal('Delete tanker', <DeleteConfirm tanker={t} onDelete={handleDelete} />, 'sm')}>
                  <FaTrash />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
